import {
  zonedTimeToUtc,
  utcToZonedTime,
  format,
  formatInTimeZone,
} from "date-fns-tz";
import { endOfDay, startOfDay } from "date-fns";
import { TIMEZONE } from "config/headers";
const militarDateTimeFormat = "MM/dd/yyyy HH:mm";

export const standardDateTimeFormat = "MM/dd/yyyy hh:mm a";
export const standardDateTimeWithSecondsFormat = "MM/dd/yyyy hh:mm:ss a";
export const standardDateFormat = "MM/dd/yyyy";
export const isoDateFormat = "yyyy-MM-dd";
export const dateTimeLocalFormat = "yyyy-MM-dd'T'HH:mm";

export const getUtcDateString = (dateTime = new Date()): string =>
  formatInTimeZone(dateTime, "UTC", isoDateFormat);

// Date-only values (C# DateOnly) arrive as UTC midnight. Formatting them in the local
// zone shows the previous day in negative offsets, so they must be read back in UTC.
export const formatDateOnly = (
  date: Date | null | undefined,
): string | undefined =>
  date ? formatInTimeZone(date, "UTC", standardDateFormat) : undefined;

export const convertToUTC = (dateTime: Date | undefined): Date | undefined => {
  if (dateTime) {
    return new Date(
      format(zonedTimeToUtc(dateTime, TIMEZONE!), militarDateTimeFormat),
    );
  }
  return undefined;
};

export const getUtcDate = (
  dateTime: Date | undefined,
  options?: { endOfDay?: boolean },
): Date | undefined => {
  if (!dateTime) return undefined;

  const adjustedDate = options?.endOfDay
    ? endOfDay(dateTime)
    : startOfDay(dateTime);

  return new Date(
    Date.UTC(
      adjustedDate.getFullYear(),
      adjustedDate.getMonth(),
      adjustedDate.getDate(),
      adjustedDate.getHours(),
      adjustedDate.getMinutes(),
      adjustedDate.getSeconds(),
      adjustedDate.getMilliseconds(),
    ),
  );
};

export function toUtcMidnight(date?: Date | null): Date | null {
  if (!date) return null;
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
}

export const convertToZonedDate = (
  dateTime: Date | undefined,
): Date | undefined => {
  if (dateTime) {
    return new Date(
      format(utcToZonedTime(dateTime, TIMEZONE!), standardDateFormat),
    );
  }
  return undefined;
};

export const convertToZonedDateTime = (
  dateTime: Date | undefined,
): string | undefined => {
  if (dateTime) {
    return format(
      utcToZonedTime(dateTime, TIMEZONE!),
      standardDateTimeWithSecondsFormat,
    );
  }
  return undefined;
};

export const convertToZonedTime = (
  dateTime: Date | undefined,
): string | undefined => {
  if (dateTime) {
    return format(utcToZonedTime(dateTime, TIMEZONE!), "HH:mm:ss");
  }
  return undefined;
};

export const convertToZonedDateTimeLocal = (
  dateTime: Date | undefined,
): string | undefined => {
  if (dateTime) {
    return format(utcToZonedTime(dateTime, TIMEZONE!), dateTimeLocalFormat);
  }
  return undefined;
};

export const dateToStandardFormat = (dateTime: Date): string => {
  return format(dateTime, standardDateTimeFormat);
};

export const convertDateTimeTo12hrFormat = (
  dateTime?: Date,
): Date | undefined => {
  return dateTime
    ? new Date(format(dateTime, standardDateTimeFormat))
    : undefined;
};

export const addDays = (dateTime?: Date, days?: number): Date | undefined => {
  if (!dateTime || !days) return undefined;

  dateTime.setDate(dateTime.getDate() + days!);
  return dateTime;
};

export const getPreviousDay = (date: Date | undefined): Date | undefined => {
  if (!date) {
    return undefined;
  }

  const previous = new Date(date?.getTime());
  previous.setDate(date?.getDate() - 1);

  return previous;
};

export const subtractHours = (date: Date, hours: number): Date | undefined => {
  if (!date) {
    return undefined;
  }

  date.setHours(date?.getHours() - hours);

  return date;
};
